/* eslint-disable react-hooks/exhaustive-deps */
"use client";
import { Size } from "@/api/AppApi/common-interfaces/api-enums";
import { useGetCompanyQuery } from "@/api/AppApi/services/company";
import { useGetAllProductsInfiniteQuery } from "@/api/AppApi/services/product";
import { useEffect, useState } from "react";
import Separator from "../../../lib/Separator";
import ProductCarousel from "@/app/components/common-builder/product/product-carousel/ProductCarousel";
import ProductGrid from "@/app/components/common-builder/product/product-grids/ProductGrid";
import { useInView } from "react-hook-inview";
import { Spinner } from "react-bootstrap";
import BundleAssociatedProduct from "./bundle-associated-product";

export enum AssociatedProductsTypes {
  upsell,
  related,
  similar,
  cross_sell,
}

interface Props {
  type: AssociatedProductsTypes;
  id: number | string | number[];
}

const AssociatedProducts: React.FC<Props> = ({ type, id }) => {
  const [isLastProductInSlider, setIsLastProductInSlider] = useState(false);
  const { data: companyData } = useGetCompanyQuery();
  const theme = companyData?.result?.theme;
  const components = theme?.components;

  const size = components?.product?.product_box_style ?? Size.small;
  const productSize = size;

  const ids = Array.isArray(id) ? id.join(",") : id;

  const productSettings = {
    [AssociatedProductsTypes.upsell]: {
      title: components?.product?.upsell_products_title,
      asGrid: components?.product?.upsell_products_asgrid,
      view: components?.product?.show_upsell_products,
      params: { upsell_products: ids },
    },
    [AssociatedProductsTypes.related]: {
      title: components?.product?.related_products_title,
      asGrid: !components?.product?.related_products_asgrid,
      view: components?.product?.show_related_products,
      params: { related_products: ids },
    },
    [AssociatedProductsTypes.similar]: {
      title: components?.product?.similar_products_title,
      asGrid: components?.product?.similar_products_asgrid,
      view: components?.product?.show_similar_products,
      params: { similar_products: ids },
    },
    [AssociatedProductsTypes.cross_sell]: {
      title: components?.cart?.crosssell_products_title,
      asGrid: components?.cart?.crosssell_products_asgrid,
      view: components?.cart?.show_crosssell_products,
      params: { crosssell_products: ids },
    },
  };

  const { title, asGrid, view, params } = productSettings[type];
  const { hasNextPage, isFetchingNextPage, fetchNextPage, data } =
    useGetAllProductsInfiniteQuery({
      params,
      enabled: view,
    });

  const products = data?.pages?.flatMap((page) => page.result.data) || [];

  const fetchMore = () => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  };

  const [ref, isInView] = useInView();

  useEffect(() => {
    if (isInView) {
      fetchMore();
    }
  }, [isInView]);

  useEffect(() => {
    if (isLastProductInSlider) {
      fetchMore();
      setIsLastProductInSlider(false);
    }
  }, [isLastProductInSlider]);
  const bundles = products.filter((product) => product.type === "bundle");
  const otherProducts = products.filter((product) => product.type !== "bundle");
  if (!view || products.length === 0) return null;

  return (
    <div>
      <Separator startLabel={title ?? ""} marginBottom={16} />
      {asGrid ? (
        <>
          <ProductGrid
            products={{
              data: otherProducts,
              configurations: { style_type: size },
            }}
            theme={theme!}
          />
          <div ref={ref} className="w-100 d-flex justify-content-center mt-3">
            {hasNextPage && <Spinner />}
          </div>
        </>
      ) : (
        <ProductCarousel
          setIslastProduct={setIsLastProductInSlider}
          products={{
            data: otherProducts,
            configurations: { style_type: productSize },
          }}
          theme={theme!}
        />
      )}
      <div className=" d-flex flex-column gap-4">
        {bundles?.map((bundle) => (
          <BundleAssociatedProduct key={bundle.id} id={bundle.id} />
        ))}
      </div>
    </div>
  );
};

export default AssociatedProducts;
